import { forwardRef, useCallback, useMemo } from 'react'
import { m, LazyMotion, domAnimation, useAnimation, Variants } from 'framer-motion'
import Image from 'next/image'

import BackgroundImage from '../../../assets/backgrounds/we-are-olby.webp'
import { UniverseExpansionFrame } from '../../../assets/icons'

import { TimelineItem } from './timeline'
import styles from './styles.module.scss'

// Animations
const ANIMATION_PROPS = {
  variants: {
    start: { y: '50%', opacity: 0.1 },
    end: {
      y: '0%',
      opacity: 1,
      transition: { type: 'spring', bounce: 0 },
    },
  },
  initial: 'start',
  whileInView: 'end',
  viewport: { once: true, amount: 0.6 },
}

const CONTENT_VARIANTS: Variants = {
  start: { opacity: 0.1 },
  end: { opacity: 1, transition: { type: 'spring', bounce: 0, when: 'beforeChildren' } },
}

export const AnUniverseInExpansion = forwardRef<HTMLDivElement>((_, ref) => {
  const contentControl = useAnimation()
  const firstItem = useAnimation()
  const firstLine = useAnimation()
  const secondItem = useAnimation()
  const secondLine = useAnimation()
  const thirdItem = useAnimation()
  const thirdLine = useAnimation()
  const fourthItem = useAnimation()
  const fourthLine = useAnimation()
  const fifthItem = useAnimation()

  const onEnterView = useCallback(async () => {
    await contentControl.start('end')
    await firstItem.start('end')
    firstLine.start('end')
    await secondItem.start('end')

    secondLine.start('end')
    await thirdItem.start('end')
    thirdLine.start('end')
    await fourthItem.start('end')
    fourthLine.start('end')
    fifthItem.start('end')
  }, [
    contentControl,
    fifthItem,
    firstItem,
    firstLine,
    fourthItem,
    fourthLine,
    secondItem,
    secondLine,
    thirdItem,
    thirdLine,
  ])

  const TIMELINE_DATA = useMemo(
    () => [
      {
        year: '2018',
        description:
          'We started as a software development company called Jungle Devs creating digital products with Silicon Valley and NY-based startups. Since day one what set us apart was our mentored-based program for training and upskilling new talent that allowed us to boost our talent careers while delivering value to global companies. ',
        textAnimate: firstItem,
        lineAnimate: firstLine,
      },
      {
        year: '2019',
        description:
          "By focusing on delivering best-in-class software, we organically grew our partner companies' network in the US, and then Canada, the UK, Australia, Poland, and South Africa.",
        textAnimate: secondItem,
        lineAnimate: secondLine,
      },
      {
        year: '2020',
        description:
          'We have already trained over 100 developers to work on international projects with globally distributed teams.',
        textAnimate: thirdItem,
        lineAnimate: thirdLine,
      },
      {
        year: '2021',
        description:
          "We understood that we have had great processes to guide developers on their path to success while working with global companies. However, Brazil's talent pool was much bigger than our initial focus, with many talented individuals looking for such opportunities. We were then acquired by Trybe who partnered with our vision to expand our horizons.",
        textAnimate: fourthItem,
        lineAnimate: fourthLine,
      },
      {
        year: '2022',
        description:
          "On a small scale, we opened up our community and validated a new company model with a selected group of clients. Later on, as Olby, we officially launched our community and dove into this new vision. For talent, we are building a journey that starts with an opportunity and evolves into a career booster. For companies, we're allowing them to do more with engaged talent from Brazil, while we take care of all the hustle.",
        textAnimate: fifthItem,
      },
    ],
    [
      fifthItem,
      firstItem,
      firstLine,
      fourthItem,
      fourthLine,
      secondItem,
      secondLine,
      thirdItem,
      thirdLine,
    ]
  )

  return (
    <div className={styles['section-container']} ref={ref}>
      <Image
        src={BackgroundImage}
        className={styles['background-image']}
        fill
        sizes="100vw"
        alt="Stars in the space"
      />
      <div className={styles['overlay']} />
      <LazyMotion features={domAnimation} strict>
        <div className={styles['section-content']}>
          <m.h2 className={styles['title']} {...ANIMATION_PROPS}>
            We are <span className={styles['decorated-title']}>Olby</span>
          </m.h2>
          <m.p className={styles['description']} {...ANIMATION_PROPS}>
            Just like the universe, we are in constant movement, expanding, discovering the space
            that goes beyond what is known today.
          </m.p>
          <m.div
            className={styles['timeline-container']}
            onViewportEnter={onEnterView}
            initial="start"
            animate={contentControl}
            variants={CONTENT_VARIANTS}
          >
            {TIMELINE_DATA.map(({ year, description, textAnimate, lineAnimate }) => (
              <TimelineItem
                key={year}
                year={year}
                description={description}
                textAnimate={textAnimate}
                lineAnimate={lineAnimate}
              />
            ))}
          </m.div>
          <UniverseExpansionFrame className={styles['frame']} />
        </div>
      </LazyMotion>
    </div>
  )
})

export default AnUniverseInExpansion
